/**
 * @file Client wrapper for the `downloadAnalyticsReportRunPdf` callable: asks
 * for one stored run's PDF, saves it under the filename the callable names,
 * and settles to an outcome, never throwing. Consumer:
 * `AnalyticsReportRunActions`. Each response field is checked rather than
 * asserted, so a response in another shape fails without saving a file.
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/services/firebaseFunctions';
import type { AnalyticsReportRunCallOutcome } from '@/analytics';
import type { AnalyticsReportRunRequest } from '#types';

import { analyticsCallFailure } from './analyticsCallFailure';

const savePdf = (pdf: string, filename: string): void => {
  const bytes = Uint8Array.from(atob(pdf), (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(
    new Blob([bytes], { type: 'application/pdf' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadAnalyticsReportRunPdf = async (
  request: AnalyticsReportRunRequest,
): Promise<AnalyticsReportRunCallOutcome> => {
  try {
    const callable = httpsCallable<AnalyticsReportRunRequest, unknown>(
      functions,
      'downloadAnalyticsReportRunPdf',
    );
    const { data } = await callable({
      reportId: request.reportId,
      runId: request.runId,
    });
    if (
      typeof data !== 'object' ||
      data === null ||
      !('pdf' in data) ||
      typeof data.pdf !== 'string' ||
      !('filename' in data) ||
      typeof data.filename !== 'string' ||
      !('retryAt' in data) ||
      typeof data.retryAt !== 'string'
    )
      return {
        status: 'failed',
        message: 'The PDF arrived in an unexpected shape.',
      };
    savePdf(data.pdf, data.filename);
    return { status: 'done', retryAt: data.retryAt };
  } catch (error) {
    return analyticsCallFailure(error);
  }
};
